import './App.scss';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import ScrollPageToTop from './components/common/components/ScrollPageToTop';

import Footer from './components/Footer';
import Header from './components/Header';
import SignIn from './components/SignIn';
import SignUp from './components/SignUp';
import PasswordReset from './components/PasswordReset';
import UpdatePassword from './components/UpdatePassword';
import MyInvoice from './components/MainContent/SubscriptionsAndPurchases/Subscriptions/PurchaseHistory/MyInvoice';

import PublicRoute from './routes/PublicRoute';
import PrivateRoute from './routes/PrivateRoute';

import MainContent from './components/MainContent';
import TermsOfService from './components/TermsOfService';
import PaymentMethods from './components/MainContent/PaymentMethods';
import SubscriptionsAndPurchases from './components/MainContent/SubscriptionsAndPurchases';
import TVsAndSoundBars from './components/MainContent/TVsAndSoundBars';
import Account from './components/MainContent/Account';
import OrderDetail from './components/MainContent/SubscriptionsAndPurchases/Orders/OrderDetail';
import EmailUnsubscribe from './components/EmailUnsubscribe';
import Toast from './components/common/components/Toast';
import ToastError from './components/common/components/ToastError';

import { useEffect, useState } from 'react';
import { getLocalStorage, getToken } from './utils/localStorage';
import { logout, setTermsOfServiceStatus } from './store/userSlice';
import { useDispatch } from 'react-redux';
import * as Sentry from '@sentry/react';
import { getUserConsentStatus } from './utils/accessRedirection';
import { pinchzoom } from './utils/eventListeners';
import { appConfig } from './utils/appConfig';
import Spinner from './components/common/components/Spinner';

import ReportAbuse from './components/ReportAbuse';
import PublicContainer from './components/containers/PublicContainer';
import ConfirmEmail from './components/ConfirmEmail';
import React from 'react';

function App() {
	const [isVerifyUserLogin, setIsVerifyUserLogin] = useState(true);
	const token = getToken();
	const dispatch = useDispatch();

	const isHeaderAndFooterHide = ['/en/account/my-invoice', '/en/account/unsubscribe'].includes(
		window.location.pathname
	);

	useEffect(() => {
		pinchzoom();

		if (token && !isHeaderAndFooterHide) {
			verifyUserLoginStatus();
		} else {
			setIsVerifyUserLogin(false);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const verifyUserLoginStatus = async () => {
		if (getLocalStorage('termsOfServiceStatus')) {
			setIsVerifyUserLogin(false);
		} else {
			getTosStatus();
		}
	};

	const getTosStatus = () => {
		getUserConsentStatus(appConfig.redirectConsentIds)
			.then((acceptedTerms: any) => {
				if (acceptedTerms === 'Failed') {
					dispatch(logout());
				} else {
					dispatch(setTermsOfServiceStatus(acceptedTerms));
				}
				setIsVerifyUserLogin(false);
			})
			.catch(() => {
				dispatch(logout());
				setIsVerifyUserLogin(false);
			});
	};

	const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

	return (
		<div id="main-container" className="accout-login">
			<BrowserRouter basename="/en/account">
				<ScrollPageToTop />

				<Toast />
				<ToastError />

				{!isHeaderAndFooterHide && (
					<div className="container">
						<Header />
					</div>
				)}

				{isVerifyUserLogin ? (
					<div className="landing-page">
						<Spinner />
					</div>
				) : (
					<SentryRoutes>
						<Route
							path="/confirm-email"
							element={
								<PrivateRoute>
									<ConfirmEmail />
								</PrivateRoute>
							}
						/>
						<Route
							path="/login"
							element={
								<PublicRoute>
									<SignIn />
								</PublicRoute>
							}
						/>
						<Route
							path="/my-invoice"
							element={
								<PrivateRoute>
									<MyInvoice />
								</PrivateRoute>
							}
						/>
						<Route
							path="/password-reset"
							element={
								<PublicRoute>
									<PasswordReset />
								</PublicRoute>
							}
						/>
						<Route
							path="/password-update"
							element={
								<PrivateRoute>
									<UpdatePassword />
								</PrivateRoute>
							}
						/>
						<Route
							path="/sign-up"
							element={
								<PublicRoute>
									<SignUp />
								</PublicRoute>
							}
						/>
						<Route
							path="/terms-of-service"
							element={
								<PrivateRoute>
									<TermsOfService />
								</PrivateRoute>
							}
						/>
						<Route path="/unsubscribe" element={<EmailUnsubscribe />} />
						<Route
							path="/viziogram/report"
							element={
								<PublicContainer>
									<ReportAbuse />
								</PublicContainer>
							}
						/>
						<Route
							path="/"
							element={
								<PrivateRoute>
									<MainContent />
								</PrivateRoute>
							}>
							<Route path="/" element={<Account />} />
							<Route path="payment-methods" element={<PaymentMethods />} />
							<Route path="my-subscriptions" element={<SubscriptionsAndPurchases />} />
							<Route path="order-details" element={<OrderDetail />} />
							<Route path="devices" element={<TVsAndSoundBars />} />
						</Route>
						<Route path="*" element={<Navigate to="/" />} />
					</SentryRoutes>
				)}

				{!isHeaderAndFooterHide && <Footer />}
			</BrowserRouter>
		</div>
	);
}

export default Sentry.withProfiler(App);
